import { observable, action } from 'mobx';
import { models } from '@kurtosys/ksys-app-template';
import { Stores } from '../models/Stores';
import { DataStore, QueryStore, ConfigStore } from '.';
import { Services } from '../services';
import { getCulturedDisclaimerFromResponse } from '../utils/disclaimers';

export class GlobalDisclaimerStore {
    @observable.ref
    static rawDisclaimers: Array<models.api.common.TDisclaimerResponse>;

    @observable.ref
    static disclaimerMap: Map<string, string> = new Map();

    @observable.ref
    static loaded: boolean = false;

    @action
    static async load(
        sharedStores: Stores,
        config: ConfigStore = sharedStores.data.config,
        culture: string = sharedStores.data.culture
    ) {
        try {
            const dataStore: DataStore = sharedStores.data;
            const services: Services = dataStore.services;
            const queryStore: QueryStore = sharedStores.query;

            if (!GlobalDisclaimerStore.loaded && services) {
                // fetch raw disclaimer response from the API
                GlobalDisclaimerStore.rawDisclaimers = await services.getGlobalDisclaimers(
                    config &&
                        config.globalConfig &&
                        config.globalConfig.disclaimerTypes
                );

                if (
                    GlobalDisclaimerStore.rawDisclaimers &&
                    GlobalDisclaimerStore.rawDisclaimers.length > 0
                ) {
                    if (GlobalDisclaimerStore.disclaimerMap) {
                        GlobalDisclaimerStore.disclaimerMap.clear();
                    } else {
                        GlobalDisclaimerStore.disclaimerMap = new Map();
                    }

                    // map raw disclaimer response to disclaimer type for current target/default culture
                    GlobalDisclaimerStore.rawDisclaimers.forEach(
                        disclaimerResponse => {
                            const culturedDisclaimer = getCulturedDisclaimerFromResponse(
                                sharedStores,
                                disclaimerResponse.disclaimers
                            );

                            if (culturedDisclaimer) {
                                GlobalDisclaimerStore.disclaimerMap.set(
                                    disclaimerResponse.disclaimerType,
                                    culturedDisclaimer
                                );
                            }
                        }
                    );
                    queryStore.loadDefaultContext();
                }
            }
        } catch (e) {
            console.warn(e);
        }
    }

    static has = (disclaimerType: string) => {
        return (
            GlobalDisclaimerStore.disclaimerMap &&
            GlobalDisclaimerStore.disclaimerMap.has(disclaimerType)
        );
    };

    /**
     * Return disclaimer text for disclaimer type provided
     *
     * @static
     */
    static get = (disclaimerType: string) => {
        return (
            GlobalDisclaimerStore.disclaimerMap &&
            GlobalDisclaimerStore.disclaimerMap.get(disclaimerType)
        );
    };

    /**
     * Return a list of disclaimer texts for the list of disclaimer types provided
     *
     * @static
     */
    static getSome = (disclaimerTypes = []) => {
        if (GlobalDisclaimerStore.disclaimerMap) {
            return disclaimerTypes
                .map(type => GlobalDisclaimerStore.disclaimerMap.get(type))
                .filter(disclaimer => disclaimer !== undefined);
        }
        return [];
    };

    /**
     * Return a dictionary obj of disclaimer texts mapped to the disclaimer type
     *
     * @static
     */
    static getFilteredDictionary = (disclaimerTypes = []) => {
        if (GlobalDisclaimerStore.disclaimerMap) {
            return disclaimerTypes.reduce(
                (filteredDictionary, disclaimerType) => {
                    const text = GlobalDisclaimerStore.disclaimerMap.get(
                        disclaimerType
                    );
                    if (text !== undefined) {
                        filteredDictionary[disclaimerType] = text;
                    }
                    return filteredDictionary;
                },
                {}
            );
        }
        return {};
    };
}



// WEBPACK FOOTER //
// ./src/stores/GlobalDisclaimerStore.ts